import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ImpersonateDialogComponent } from '../../Dialogs/impersonate-dialog/impersonate-dialog.component';
import { AvAvatarComponent } from '../../../../Shared/Components/av-avatar/av-avatar.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { UserDetailFacade } from '../../../../../Facades/Users/user-detail.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import { ImpersonateDialogResult } from '../../../Interfaces/impersonate-dialog.interface';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [
    DatePipe,
    RouterLink,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvAvatarComponent,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class UserDetailComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(UserDetailFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;

  private readonly _params = toSignal(this._route.paramMap);

  readonly organizationId = computed<string>(() => this.facade.user()?.organization?.id ?? '');

  constructor() {
    this._registerUserParam();
  }

  openImpersonate(): void {
    const user = this.facade.user();

    if (!user) {
      return;
    }

    const reference = this._dialog.open(ImpersonateDialogComponent, {
      data: { userName: user.fullName, userEmail: user.email ?? this.unresolved },
    });

    reference
      .afterClosed()
      .subscribe((result?: ImpersonateDialogResult) => this._applyImpersonation(result));
  }

  private _registerUserParam(): void {
    effect(() => {
      const userId = this._params()?.get('id') ?? '';

      this._loadUser(userId);
    });
  }

  private _loadUser(userId: string): void {
    this.facade.load(userId);
  }

  private _applyImpersonation(result?: ImpersonateDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.impersonate(result.reason);
  }
}
